import React, { useEffect, useState } from "react";
import { MdEdit, MdDelete } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { emailDomainAuthenticatorActions } from "../../actions/emailDomainAuthenticator.actions";
import Utils from "../../utils";
import Title from "../common/Title";
import EnhancedGrid from "../common/EnhancedGrid";
import GridHelper from "../helpers/gridHelper";
import { FaKey, FaShieldAlt } from "react-icons/fa";
import ConfirmModal from "../common/ConfirmModal";
import Subtitle from "../common/Subtitle";
import { Button } from "react-bootstrap";
import AuthenticatorGridFilter from "./AuthenticatorGridFilter";

function AuthenticatorsGrid({ presetClientId, onEditAuthenticator, onAddAuthenticator, hideTitle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);
  const [deleteAuthenticatorName, setDeleteAuthenticatorName] = useState("");
  const [deleteAuthenticatorId, setDeleteAuthenticatorId] = useState("");
  const [selectedAuthenticator, setSelectedAuthenticator] = useState(null);
  const emailDomainAuthenticator = useSelector(
    (state) => state.emailDomainAuthenticator
  );

  const filterInitialState = {
    emailDomain: "",
  };
  const [filter, setFilter] = useState(filterInitialState);

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);
    const clientIdToLoad = presetClientId ?? queryParams["clientId"];
    if (clientIdToLoad) {
      queryParams.clientId = clientIdToLoad;
    }

    dispatch(emailDomainAuthenticatorActions.get(queryParams));

    // Initialize filter from URL
    setFilter({
      emailDomain: queryParams["emailDomain"] || "",
    });

    // eslint-disable-next-line
  }, [location, presetClientId]);

  const handleClearFilters = () => {
    GridHelper.handleSearch(filterInitialState, location, navigate);
  };

  const handleDeleteAuthenticator = () => {
    setConfirmDeleteModalShow(false);
    let requestParameters = {
      id: deleteAuthenticatorId,
    };

    dispatch(emailDomainAuthenticatorActions.remove(requestParameters));
  };

  let displayColumns = ["emailDomain", "authenticatorType", "createdOn"];

  const data = emailDomainAuthenticator.paginatedResult?.data.map((a) => {
    return {
      ...a,
      createdOn: Utils.convertToDateOnly(a.createdOn),
    };
  });

  const effectiveClientId = presetClientId ?? emailDomainAuthenticator?.request?.clientId;

  return (
    <>
      {!hideTitle && (
        <>
          <Title text={"Authenticators"} icon={<FaKey className="mb-1" />} />

          {!emailDomainAuthenticator.loading && (
            <Subtitle text={`Client ${emailDomainAuthenticator?.request?.clientId}`} />
          )}
        </>
      )}

      <AuthenticatorGridFilter filter={filter} onClearFilters={handleClearFilters} />

      {/* Action Buttons Bar */}
      <div className="d-flex gap-2 mb-3 p-3 bg-light rounded"
           style={{
             opacity: selectedAuthenticator ? 1 : 0.6,
             transition: 'opacity 0.3s ease'
           }}>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedAuthenticator}
          onClick={() => {
            if (selectedAuthenticator) {
              onEditAuthenticator
                ? onEditAuthenticator(selectedAuthenticator.id)
                : navigate(`edit?id=${selectedAuthenticator.id}`);
            }
          }}
        >
          <MdEdit className="mb-1 me-2" />
          Edit
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedAuthenticator}
          onClick={() => {
            if (selectedAuthenticator) {
              setDeleteAuthenticatorId(selectedAuthenticator.id);
              setDeleteAuthenticatorName(selectedAuthenticator.emailDomain);
              setConfirmDeleteModalShow(true);
            }
          }}
        >
          <MdDelete className="mb-1 me-2" />
          Delete
        </Button>

        <Button
          variant="primary"
          size="sm"
          className="ms-auto"
          onClick={() =>
            onAddAuthenticator
              ? onAddAuthenticator(effectiveClientId)
              : navigate(`add?clientId=${effectiveClientId}`)
          }
        >
          Add Authenticator
        </Button>

        {selectedAuthenticator && (
          <div className="align-self-center">
            <small className="text-muted">
              Selected: <strong>{selectedAuthenticator.emailDomain}</strong>
            </small>
          </div>
        )}
      </div>

      <EnhancedGrid
        gridId="authenticator"
        fillHeight={hideTitle}
        data={data}
        displayColumns={displayColumns}
        emptyState={{
          icon: <FaShieldAlt size={80} className="text-muted mb-3" />,
          message: "No authenticators found",
          subtitle: "Configure authenticators to enable SSO for your clients.",
          action: (
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                onAddAuthenticator
                  ? onAddAuthenticator(effectiveClientId)
                  : navigate(`add?clientId=${effectiveClientId}`)
              }
            >
              Add Authenticator
            </Button>
          ),
        }}
        idColumn={"id"}
        pageNumber={(emailDomainAuthenticator.paginatedResult?.pageNumber ?? 1) - 1}
        totalPages={emailDomainAuthenticator.paginatedResult?.totalPages ?? 0}
        loading={emailDomainAuthenticator.loading}
        onPageChange={GridHelper.handlePageClick}
        onSort={GridHelper.handleSortClick}
        onRowClick={setSelectedAuthenticator}
        selectedRow={selectedAuthenticator}
      />

      <ConfirmModal
        show={confirmDeleteModalShow}
        title="Delete Email Domain Authenticator"
        question={`Are you sure you want to delete authenticator for ${deleteAuthenticatorName}?`}
        noButtonText="No"
        yesButtonText="Yes, please"
        handleClose={() => {
          setConfirmDeleteModalShow(false);
        }}
        handleConfirm={() => {
          handleDeleteAuthenticator();
        }}
      />
    </>
  );
}

export default AuthenticatorsGrid;
